<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

// Templates carry their own component list from Meta; a template counts as a Flow template
// when any of its BUTTONS entries has type FLOW — that's the button that broke sending
// (see Whatsapp::TemplateProcessorService#process_button_components) and the one this page
// exists to help diagnose/configure.
const templateType = template => {
  const buttonsComponent = (template.components || []).find(
    component => component.type === 'BUTTONS'
  );
  const hasFlowButton = (buttonsComponent?.buttons || []).some(
    button => button.type === 'FLOW'
  );
  if (hasFlowButton) return 'FLOW';

  const headerComponent = (template.components || []).find(
    component => component.type === 'HEADER'
  );
  const mediaFormats = ['IMAGE', 'VIDEO', 'DOCUMENT'];
  if (mediaFormats.includes(headerComponent?.format)) return 'MEDIA';

  return 'TEXT';
};

const flowIdFor = template => {
  const buttonsComponent = (template.components || []).find(
    component => component.type === 'BUTTONS'
  );
  return (buttonsComponent?.buttons || []).find(
    button => button.type === 'FLOW'
  )?.flow_id;
};

const templates = computed(() =>
  (props.inbox.message_templates || []).map(template => ({
    ...template,
    type: templateType(template),
    flowId: flowIdFor(template),
  }))
);

const flowTemplates = computed(() =>
  templates.value.filter(template => template.type === 'FLOW')
);

const typeBadgeClass = {
  FLOW: 'bg-n-amber-3 text-n-amber-11',
  MEDIA: 'bg-n-blue-3 text-n-blue-11',
  TEXT: 'bg-n-slate-3 text-n-slate-11',
};

// Key generation
const isGeneratingKey = ref(false);
const keyRegisteredAt = ref(props.inbox.flow_public_key_registered_at);
const isKeyRegistered = computed(() => Boolean(keyRegisteredAt.value));

const generateEncryptionKey = async () => {
  isGeneratingKey.value = true;
  try {
    const response = await InboxesAPI.generateFlowEncryptionKey(
      props.inbox.id
    );
    keyRegisteredAt.value = response.data.flow_public_key_registered_at;
    useAlert(t('INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.ERROR')
    );
  } finally {
    isGeneratingKey.value = false;
  }
};

// Endpoint URL — pasted into the Flow Builder's own endpoint field; Meta's "Test endpoint"
// button there runs the real encryption handshake, nothing here can simulate that client-side.
const endpointUrl = computed(
  () =>
    `${window.location.origin}/webhooks/whatsapp_flow/${props.inbox.phone_number}`
);

const copyEndpointUrl = async () => {
  await copyTextToClipboard(endpointUrl.value);
  useAlert(t('INBOX_MGMT.FLOWS_CONFIGURATION.ENDPOINT.COPIED'));
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsFieldSection
      :label="t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TITLE')"
      :help-text="t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.DESCRIPTION')"
    >
      <div class="overflow-x-auto">
        <table class="w-full text-left text-label-small">
          <thead>
            <tr class="text-n-slate-11">
              <th class="py-2 pr-4">
                {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TABLE.NAME') }}
              </th>
              <th class="py-2 pr-4">
                {{
                  t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TABLE.CATEGORY')
                }}
              </th>
              <th class="py-2 pr-4">
                {{
                  t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TABLE.LANGUAGE')
                }}
              </th>
              <th class="py-2 pr-4">
                {{
                  t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TABLE.STATUS')
                }}
              </th>
              <th class="py-2 pr-4">
                {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TABLE.TYPE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="template in templates"
              :key="template.id || template.name"
              class="border-t border-n-weak"
            >
              <td class="py-2 pr-4 text-n-slate-12">{{ template.name }}</td>
              <td class="py-2 pr-4">{{ template.category }}</td>
              <td class="py-2 pr-4">{{ template.language }}</td>
              <td class="py-2 pr-4">{{ template.status }}</td>
              <td class="py-2 pr-4">
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-label-small"
                  :class="typeBadgeClass[template.type]"
                >
                  {{
                    t(
                      `INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.TYPE_BADGE.${template.type}`
                    )
                  }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!templates.length" class="text-n-slate-11 py-2">
          {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.EMPTY') }}
        </p>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="flowTemplates.length"
      :label="t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.FLOW_ID')"
    >
      <div class="flex flex-col gap-2">
        <div
          v-for="template in flowTemplates"
          :key="template.id || template.name"
          class="flex items-center justify-between gap-4 rounded-md bg-n-alpha-1 px-3 py-2"
        >
          <div class="flex flex-col">
            <span class="text-n-slate-12">{{ template.name }}</span>
            <span class="text-n-slate-11 text-label-small">
              {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.FLOW_ID') }}:
              {{ template.flowId }}
            </span>
          </div>
          <div class="flex flex-col items-end">
            <span class="text-n-slate-11 text-label-small">
              {{
                t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.HANDLER_LABEL')
              }}
            </span>
            <!-- No concrete data_exchange handlers exist yet (Whatsapp::Flows::ScreenRouterService::FLOW_HANDLERS
                 is empty) — every Flow template is navigate-only until the first handler ships. -->
            <span class="text-n-slate-12">
              {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.TEMPLATES.HANDLER_NONE') }}
            </span>
          </div>
        </div>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.TITLE')"
      :help-text="
        t('INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.DESCRIPTION')
      "
    >
      <div class="flex items-center gap-3">
        <div
          class="inline-flex w-fit items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-label-small"
          :class="isKeyRegistered ? 'text-n-teal-11' : 'text-n-slate-11'"
        >
          <span
            class="size-1.5 rounded-full"
            :class="isKeyRegistered ? 'bg-n-teal-9' : 'bg-n-slate-9'"
          />
          {{
            t(
              isKeyRegistered
                ? 'INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.REGISTERED'
                : 'INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.NOT_REGISTERED'
            )
          }}
        </div>
        <NextButton
          :is-loading="isGeneratingKey"
          size="sm"
          @click="generateEncryptionKey"
        >
          {{
            t(
              isKeyRegistered
                ? 'INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.REGENERATE_BUTTON'
                : 'INBOX_MGMT.FLOWS_CONFIGURATION.ENCRYPTION_KEY.GENERATE_BUTTON'
            )
          }}
        </NextButton>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="t('INBOX_MGMT.FLOWS_CONFIGURATION.ENDPOINT.TITLE')"
      :help-text="t('INBOX_MGMT.FLOWS_CONFIGURATION.ENDPOINT.DESCRIPTION')"
    >
      <div class="flex items-center gap-2">
        <code
          class="flex-1 truncate rounded-md bg-n-alpha-1 px-2 py-1.5 text-label-small text-n-slate-12"
        >
          {{ endpointUrl }}
        </code>
        <NextButton size="sm" variant="outline" @click="copyEndpointUrl">
          {{ t('INBOX_MGMT.FLOWS_CONFIGURATION.ENDPOINT.COPY_BUTTON') }}
        </NextButton>
      </div>
    </SettingsFieldSection>
  </div>
</template>
